import type { Optional } from '@festina-life/core'
import { FPlusCodeContainer } from '@festina-life/fplus'
import { IconButton } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { RawCheckbox } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Typography } from '@festina-life/ui/structure'

import { DetailsLayout } from '../../components/detailsLayout/DetailsLayout'
import { DocumentationEntry } from '../../components/documentationEntry/DocumentationEntry'
import { InlineTextEditor } from '../../components/inlineTextEditor/InlineTextEditor'
import { EditableMarkdownViewer } from '../../components/markdownEditor/EditableMarkdownViewer'
import { useIsInlineEditingAvailable } from '../../hooks/useIsInlineEditingAvailable'
import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import {
  cloneEnumDefinitionWithValue,
  workspaceOperationFromEnumDefinition,
} from './helpers'
import { useUpdateEnumDefinition } from './useUpdateEnumDefinition'

type Props = {
  value: EnumDefinition
  onClose: () => void
}

export const EnumDefinitionDetails = ({ value, onClose }: Props) => {
  const translate = useTranslate()
  const updateEnumDefinition = useUpdateEnumDefinition(value)
  const isEditable = useIsInlineEditingAvailable()
  const isNameEditable =
    isEditable &&
    value.isDraft &&
    value.literals.every(
      (literal) => literal.type === 'configured' && literal.isDraft,
    )

  const workspaceOperation = workspaceOperationFromEnumDefinition(value)
  const currentValueWithWorkpaceOperation: EnumDefinition = {
    ...value,
    internalOperation: workspaceOperation,
  }

  return (
    <div className="size-full overflow-hidden flex min-h-0 flex-col">
      <div className="flex items-center justify-between gap-x-2">
        <Typography size="lg" weight="semibold" className="p-2">
          {value.name}
        </Typography>
        <IconButton
          onClick={onClose}
          iconSize="md"
          tip={translate('close')}
          iconName="XCircle"
        />
      </div>
      <div className="flex-1 flex flex-col gap-2 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-2">
          {value.errors.length ? (
            <div className="p-2 flex flex-col gap-2">
              {value.errors.map(({ error }) => (
                <Alert key={error.errorMessage} severity="error">
                  {error.errorMessage}
                </Alert>
              ))}
            </div>
          ) : null}
          <DetailsLayout>
            <DocumentationEntry
              title={translate('enumViewer.details.enum.name')}
              value={
                <FPlusCodeContainer className="group relative">
                  {isNameEditable ? (
                    <InlineTextEditor
                      initialValue={value.name}
                      onSave={async (nextName) => {
                        if (nextName) {
                          await updateEnumDefinition({
                            ...value,
                            internalOperation: {
                              ...workspaceOperation,
                              entity: {
                                _type: 'EnumDefinitionInOut',
                                ...workspaceOperation.entity,
                                name: nextName,
                              },
                            },
                          })
                        }
                      }}
                    />
                  ) : (
                    <div>{value.name}</div>
                  )}
                </FPlusCodeContainer>
              }
            />
            <DocumentationEntry
              title={translate('enumViewer.details.enum.description')}
              value={
                <EditableMarkdownViewer
                  value={value.internalOperation?.entity?.description}
                  onSave={
                    isEditable
                      ? async (nextDescription: Optional<string>) => {
                          await updateEnumDefinition(
                            cloneEnumDefinitionWithValue(
                              currentValueWithWorkpaceOperation,
                              {
                                _type: 'EnumDefinitionInOut',
                                ...workspaceOperation.entity,
                                description: nextDescription,
                              },
                            ),
                          )
                        }
                      : undefined
                  }
                />
              }
            />
            <DocumentationEntry
              title={translate('enumViewer.details.literal.ordinal')}
              value={
                <RawCheckbox
                  checked={!!value.internalOperation?.entity?.ordinal}
                  onChange={
                    isEditable
                      ? async (isNextOrdinal) => {
                          await updateEnumDefinition(
                            cloneEnumDefinitionWithValue(
                              currentValueWithWorkpaceOperation,
                              {
                                _type: 'EnumDefinitionInOut',
                                ...workspaceOperation.entity,
                                ordinal: isNextOrdinal,
                              },
                            ),
                          )
                        }
                      : undefined
                  }
                  disabled={!isEditable}
                />
              }
            />
            <DocumentationEntry
              title={translate('enumViewer.details.literal.deprecated')}
              value={
                <RawCheckbox
                  checked={!!value.internalOperation?.entity?.deprecated}
                  onChange={
                    isEditable
                      ? async (isNextDeprecated) => {
                          await updateEnumDefinition(
                            cloneEnumDefinitionWithValue(
                              currentValueWithWorkpaceOperation,
                              {
                                _type: 'EnumDefinitionInOut',
                                ...workspaceOperation.entity,
                                deprecated: isNextDeprecated,
                              },
                            ),
                          )
                        }
                      : undefined
                  }
                  disabled={!isEditable}
                />
              }
            />
          </DetailsLayout>
        </div>
      </div>
    </div>
  )
}
